// M22 "isolate an ingredient": one colour-range or object mask baked into one channel of a mask raster that
// covers the whole source (the same raster size and sampling as brush masks), so the colour shader, the
// overlay (O) and the export all read the same weights.
//   weight = colour range (OkLCh distance to the samples, features/colourMask.ts) or the object matte
//            → paint in (max) → paint out (× 1 − remove) → × the optional linear / radial limit.
import { COLOR_FNS, HEADER, OKLAB_FNS } from './common';

export const PICK_MASK = `${HEADER}
${COLOR_FNS}
${OKLAB_FNS}
uniform sampler2D uSrc;       // the linear preview of the source (premultiplied)
uniform vec2 uSrcTexel;       // 1 / preview size
uniform vec2 uOutSize;
uniform int uMode;            // 0 colour range, 1 object matte
uniform int uN;               // samples (≤ 8)
uniform vec3 uSample[8];      // x, y (source-normalised), kind: 0 = read the photo there, 1 = uLch given
uniform vec3 uLch[8];
uniform vec3 uTol;            // hue degrees, chroma, lightness
uniform vec2 uEdge;           // inner, outer (tolerance units)
uniform sampler2D uObj;       // object matte (R), whole source
uniform sampler2D uRefine;    // R = paint in, G = paint out
uniform int uLimit;           // -1 none, 0 linear, 1 radial
uniform vec4 uLimA;
uniform vec4 uLimB;
uniform vec2 uSrcDim;         // full source size (round ellipses)
out vec4 o;

float sm(float e0, float e1, float x) { float t = clamp((x - e0) / max(e1 - e0, 1e-6), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
vec3 lchAt(vec2 uv) {
  vec3 acc = vec3(0.0);
  for (int j = -1; j <= 1; j++)
    for (int i = -1; i <= 1; i++) {
      vec4 s = texture(uSrc, clamp(uv + vec2(float(i), float(j)) * uSrcTexel, vec2(0.0), vec2(1.0)));
      acc += s.a > 1e-6 ? s.rgb / s.a : vec3(0.0);
    }
  return toLch(p3ToOklab(max(acc / 9.0, vec3(0.0))));
}
float hueDelta(float a, float b) { float d = mod(abs(a - b), 360.0); return d > 180.0 ? 360.0 - d : d; }
float colourW(vec3 px) {
  float w = 0.0;
  for (int i = 0; i < 8; i++) {
    if (i >= uN) break;
    vec3 s = uSample[i].z > 0.5 ? uLch[i] : lchAt(uSample[i].xy);
    float k = clamp(min(px.y, s.y) / 0.04, 0.0, 1.0);
    float dh = hueDelta(px.z, s.z) * k / uTol.x;
    float dc = abs(px.y - s.y) / uTol.y;
    float dl = abs(px.x - s.x) / uTol.z;
    w = max(w, 1.0 - sm(uEdge.x, uEdge.y, sqrt(dh * dh + dc * dc + dl * dl)));
  }
  return w;
}
float limitW(vec2 uv) {
  if (uLimit < 0) return 1.0;
  vec2 p = uv * uSrcDim;
  if (uLimit == 0) {
    vec2 a = uLimA.xy * uSrcDim, b = uLimA.zw * uSrcDim;
    vec2 d = b - a;
    float t = dot(p - a, d) / max(dot(d, d), 1e-6);
    return 1.0 - sm(0.0, 1.0, t);
  }
  vec2 c = uLimA.xy * uSrcDim;
  vec2 ax = max(uLimA.zw * uSrcDim, vec2(1e-3));
  float cs = cos(uLimB.x), sn = sin(uLimB.x);
  vec2 q = p - c;
  q = vec2(cs * q.x + sn * q.y, -sn * q.x + cs * q.y);
  float d = length(q / ax);
  float fe = clamp(uLimB.y, 0.0, 1.0);
  return d <= 1.0 - fe ? 1.0 : d >= 1.0 ? 0.0 : 1.0 - sm(1.0 - fe, 1.0, d);
}
void main() {
  vec2 uv = gl_FragCoord.xy / uOutSize;
  float w;
  if (uMode == 0) {
    vec4 s = texture(uSrc, uv);
    vec3 c = s.a > 1e-6 ? s.rgb / s.a : vec3(0.0);
    w = colourW(toLch(p3ToOklab(max(c, vec3(0.0)))));
  } else {
    w = texture(uObj, uv).r;
  }
  vec4 r = texture(uRefine, uv);
  w = max(w, r.r) * (1.0 - r.g);
  o = vec4(clamp(w * limitW(uv), 0.0, 1.0));
}`;
